import type { Contact } from '@catalyst-psa/shared';
import {
  ADMIN_ACCESS_LABELS,
  AI_ACCESS_LABELS,
  SUPPORT_TICKET_ACCESS_LABELS,
  portalRoleFlagLabel,
} from '@catalyst-psa/shared';

import { Badge } from '../../components/ui/Badge.js';
import { DataState } from '../../components/ui/DataState.js';
import { useContacts } from './api.js';

interface ContactPortalTabProps {
  customerId: string;
}

function contactName(contact: Contact): string {
  return `${contact.firstName} ${contact.lastName ?? ''}`.trim();
}

function formatDateTime(value: string | null): string {
  if (!value) return 'Never';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Never' : date.toLocaleString();
}

/** Most recent portal sync across the customer's contacts. */
function lastSyncedAt(contacts: Contact[]): string | null {
  let latest: string | null = null;
  for (const contact of contacts) {
    const value = contact.portalSyncedAt;
    if (value && (latest === null || value > latest)) latest = value;
  }
  return latest;
}

function YesNo({ value }: { value: boolean }): JSX.Element {
  return <Badge tone={value ? 'green' : 'gray'}>{value ? 'Yes' : 'No'}</Badge>;
}

/**
 * Portal tab on the customer form. Everything here is read-only: the top section mirrors
 * the Catalyst Portal, the bottom section is the access the customer asked for on the
 * onboarding welcome form (reference only — it grants nothing).
 */
export function ContactPortalTab({ customerId }: ContactPortalTabProps): JSX.Element {
  const contactsQuery = useContacts({ customerId, pageSize: 200 });
  const contacts = contactsQuery.data?.data ?? [];
  const synced = lastSyncedAt(contacts);

  return (
    <div className="space-y-6 p-1">
      <DataState
        isLoading={contactsQuery.isLoading}
        isError={contactsQuery.isError}
        error={contactsQuery.error}
        onRetry={() => void contactsQuery.refetch()}
        isEmpty={contacts.length === 0}
        emptyTitle="No contacts"
        emptyDescription="Add contacts on the Contacts tab to see their portal access."
      >
        <section className="space-y-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">
                Catalyst Portal User Access (read-only)
              </h3>
              <p className="text-xs text-slate-500">
                This is only a view of the portal config. Changes are made by customer self
                administration or in the portal directly.
              </p>
            </div>
            <span className="text-xs text-slate-400">Last synced: {formatDateTime(synced)}</span>
          </div>
          <div className="space-y-2">
            {contacts.map((contact) => (
              <PortalSnapshotRow key={contact.id} contact={contact} />
            ))}
          </div>
        </section>

        <section className="space-y-2">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              Customer Requested During Onboarding (read-only)
            </h3>
            <p className="text-xs text-slate-500">
              If this customer was onboarded via an automated welcome form, we asked their
              preference for who should have access to portal, AI, billing etc. Below are the
              results. This is for reference only and will not update any real functionality.
            </p>
          </div>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2">Contact</th>
                  <th className="px-3 py-2">Support Ticket Access</th>
                  <th className="px-3 py-2">AI Access</th>
                  <th className="px-3 py-2">Admin Access</th>
                  <th className="px-3 py-2 text-center">Support</th>
                  <th className="px-3 py-2 text-center">Ticket manager</th>
                  <th className="px-3 py-2 text-center">Project</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {contacts.map((contact) => (
                  <tr key={contact.id} className="align-middle">
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">{contactName(contact)}</div>
                      <div className="text-xs text-slate-500">{contact.email ?? '—'}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {SUPPORT_TICKET_ACCESS_LABELS[contact.supportTicketAccess]}
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {AI_ACCESS_LABELS[contact.aiAccess]}
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {ADMIN_ACCESS_LABELS[contact.adminAccess]}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <YesNo value={contact.supportContact} />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <YesNo value={contact.ticketManager} />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <YesNo value={contact.projectContact} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </DataState>
    </div>
  );
}

function PortalSnapshotRow({ contact }: { contact: Contact }): JSX.Element {
  const flagKeys = Object.keys(contact.portalRoleFlags).sort();
  const matched = contact.portalUserId !== null || flagKeys.length > 0;

  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-slate-900">{contactName(contact)}</span>
        {matched ? (
          <Badge tone={contact.portalStatus === 'active' ? 'green' : 'gray'}>
            {contact.portalStatus ?? 'unknown status'}
          </Badge>
        ) : (
          <Badge tone="gray">Not found in the portal</Badge>
        )}
        <span className="text-xs text-slate-400">
          Employee ID {contact.netsuiteEmployeeId ?? '—'} · Portal user{' '}
          {contact.portalUserId ?? '—'} · Tenant {contact.portalTenant ?? '—'} · Synced{' '}
          {formatDateTime(contact.portalSyncedAt)}
        </span>
      </div>
      {matched ? (
        <dl className="mt-2 grid gap-x-6 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
          {flagKeys.length === 0 ? (
            <p className="text-xs text-slate-500">The portal returned no role flags.</p>
          ) : (
            flagKeys.map((key) => (
              <div key={key} className="flex items-center justify-between gap-2 text-sm">
                <dt className="text-slate-600">{portalRoleFlagLabel(key)}</dt>
                <dd>
                  <Badge tone={contact.portalRoleFlags[key] ? 'green' : 'gray'}>
                    {contact.portalRoleFlags[key] ? 'On' : 'Off'}
                  </Badge>
                </dd>
              </div>
            ))
          )}
        </dl>
      ) : null}
    </div>
  );
}
